import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { ReactElement } from 'react';
import type { PlanRankingRow } from '@/application/metrics';
import { getPlanRanking } from '@/infrastructure/http/metricsApi';
import { metricsKeys } from '@/infrastructure/query/metricsKeys';
import { useAuth } from '@/presentation/contexts/useAuth';
import { MetricBlock } from './MetricBlock';
import { useMetricChartTheme } from './charts/chartTheme';
import type { MetricChartTheme } from './charts/chartTheme';
import { formatCurrency, formatCurrencyCompact, formatInteger } from './metricFormatters';

interface PlanRankingBlockProps {
  tenantId: string;
  year: number;
  month: number;
  currency?: string;
}

type SortKey = 'cantidad' | 'facturacion';
type Trend = 'up' | 'down' | 'flat';

const RANGE_OPTIONS = [1, 3, 6, 12];
const MAX_BARS = 10;

interface ChartRow {
  name: string;
  value: number;
  cantidad: number;
  facturacion: number;
  trend: Trend;
}

function trendOf(row: PlanRankingRow): Trend {
  if (row.cantidad > row.cantidadPrevia) return 'up';
  if (row.cantidad < row.cantidadPrevia) return 'down';
  return 'flat';
}

/** Etiqueta lateral: valor + flecha de tendencia (unidades vs ventana anterior), afuera de la barra. */
function makePlanRankingLabel(rows: ChartRow[], theme: MetricChartTheme, format: (value: number) => string) {
  const offset = 6;
  return function PlanRankingLabel(props: {
    x?: number | string;
    y?: number | string;
    width?: number | string;
    height?: number | string;
    index?: number;
  }): ReactElement | null {
    const { x, y, width, height, index } = props;
    if (x === undefined || y === undefined || width === undefined || height === undefined || index === undefined) {
      return null;
    }
    const row = rows[index];
    if (!row) {
      return null;
    }
    const rightEdge = Number(x) + Number(width);
    const centerY = Number(y) + Number(height) / 2;
    const arrow = row.trend === 'up' ? '▲' : row.trend === 'down' ? '▼' : '=';
    const arrowColor =
      row.trend === 'up' ? theme.colors.green : row.trend === 'down' ? theme.colors.red : theme.colors.slate;

    return (
      <text x={rightEdge + offset} y={centerY} fontSize={11} textAnchor="start" dominantBaseline="central">
        <tspan fill={theme.axis}>{format(row.value)}</tspan>
        <tspan dx={6} fill={arrowColor} fontSize={12}>
          {arrow}
        </tspan>
      </text>
    );
  };
}

/**
 * Ranking de planes vendidos de la clínica: qué planes se venden más y cuáles poco.
 * Ordenable por cantidad o facturación, con selector de ventana (meses) y flecha de
 * tendencia por plan (unidades vs la ventana anterior de igual tamaño). Se auto-carga.
 */
export function PlanRankingBlock({ tenantId, year, month, currency = 'ARS' }: PlanRankingBlockProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const { token } = useAuth();
  const theme = useMetricChartTheme();
  const [months, setMonths] = useState(3);
  const [sort, setSort] = useState<SortKey>('cantidad');

  const rankingQuery = useQuery({
    queryKey: metricsKeys.planRanking(tenantId, year, month, months),
    queryFn: () => getPlanRanking(token!, { tenantId, year, month, months }),
    enabled: Boolean(token && tenantId && year && month),
  });

  const data = rankingQuery.data ?? null;
  const isLoading = rankingQuery.isLoading;
  const error = rankingQuery.error instanceof Error ? rankingQuery.error.message : null;

  const isMoney = sort === 'facturacion';
  const formatValue = (value: number) =>
    isMoney ? formatCurrency(value, locale, currency) : formatInteger(value, locale);
  const formatAxis = (value: number) =>
    isMoney ? formatCurrencyCompact(value, locale, currency) : formatInteger(value, locale);

  const chartData = useMemo<ChartRow[]>(() => {
    if (!data) {
      return [];
    }
    return data
      .map((row) => ({
        name: row.planNombre,
        value: isMoney ? row.facturacion : row.cantidad,
        cantidad: row.cantidad,
        facturacion: row.facturacion,
        trend: trendOf(row),
      }))
      .sort((current, next) => next.value - current.value)
      .slice(0, MAX_BARS);
  }, [data, isMoney]);

  // Padding del dominio para que el valor + flecha (afuera de la barra) no se corten.
  const maxValue = chartData.reduce((max, row) => Math.max(max, row.value), 0);
  const domain: [number, number] = [0, maxValue === 0 ? 1 : maxValue * 1.15];
  const chartHeight = Math.max(200, chartData.length * 44 + 24);

  const selectClass =
    'h-9 rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-700 dark:bg-gray-950 dark:text-white';

  const rangeLabel = (value: number) =>
    value === 1 ? t('metrics.planRanking.thisMonth') : t('metrics.planRanking.months', { count: value });

  return (
    <MetricBlock
      title={t('metrics.planRanking.title')}
      description={t('metrics.planRanking.description')}
      isLoading={isLoading}
      error={error}
      isEmpty={!data || data.length === 0}
      emptyMessage={t('metrics.planRanking.empty')}
    >
      {chartData.length > 0 && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <label className="sr-only" htmlFor="plan-ranking-sort">
              {t('metrics.planRanking.sortLabel')}
            </label>
            <select
              id="plan-ranking-sort"
              value={sort}
              onChange={(event) => setSort(event.target.value as SortKey)}
              className={selectClass}
            >
              <option value="cantidad">{t('metrics.planRanking.sort.cantidad')}</option>
              <option value="facturacion">{t('metrics.planRanking.sort.facturacion')}</option>
            </select>
            <label className="sr-only" htmlFor="plan-ranking-range">
              {t('metrics.planRanking.rangeLabel')}
            </label>
            <select
              id="plan-ranking-range"
              value={months}
              onChange={(event) => setMonths(Number(event.target.value))}
              className={selectClass}
            >
              {RANGE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {rangeLabel(option)}
                </option>
              ))}
            </select>
          </div>

          <div style={{ height: chartHeight }} className="w-full" role="img" aria-label={t('metrics.planRanking.ariaLabel')}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical" margin={{ top: 8, right: 76, left: 28, bottom: 0 }}>
                <CartesianGrid stroke={theme.grid} strokeDasharray="3 3" horizontal={false} />
                <XAxis
                  type="number"
                  domain={domain}
                  tick={{ fill: theme.axis, fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => formatAxis(Number(value))}
                />
                <YAxis
                  dataKey="name"
                  type="category"
                  width={140}
                  tick={{ fill: theme.axis, fontSize: 12 }}
                  tickLine={false}
                  axisLine={{ stroke: theme.grid }}
                />
                <Tooltip
                  cursor={{ fill: 'rgba(148, 163, 184, 0.16)' }}
                  contentStyle={{ background: theme.tooltipBackground, borderColor: theme.tooltipBorder, color: theme.tooltipText }}
                  labelStyle={{ color: theme.tooltipText }}
                  itemStyle={{ color: theme.tooltipText }}
                  formatter={(_value, _name, item) => {
                    const row = item?.payload as ChartRow | undefined;
                    if (!row) {
                      return '';
                    }
                    return [
                      `${formatInteger(row.cantidad, locale)} · ${formatCurrency(row.facturacion, locale, currency)}`,
                      t('metrics.planRanking.tooltipValue'),
                    ];
                  }}
                />
                <Bar dataKey="value" fill={theme.colors.indigo} radius={[0, 6, 6, 0]}>
                  <LabelList dataKey="value" content={makePlanRankingLabel(chartData, theme, formatValue)} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-400">{t('metrics.planRanking.hint')}</p>
        </div>
      )}
    </MetricBlock>
  );
}
